import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from 'axios';
import { config } from './config';

// 🟢 Fetch Store Settings Thunk
export const fetchStoreSettings = createAsyncThunk(
  'cart/fetchStoreSettings',
  async () => {
    try {
      const response = await axios.get(`${config.serverURL}/api/storeinfo`); // API call
      const { freeShipping, minOrderVal, isStoreOpen } = response.data; // Destructure only required fields
      return { freeShipping, minOrderVal, isStoreOpen }; // Return only the required data
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch store settings');
    }
  }
);

const mindisCoupon = 0;
const maxCouponDisc = 0;
const CouponName = "";

// Function to calculate totals from items
const calculateTotals = (items, freeShipping) => {
  let totalQuantity = 0;
  let totalAmount = 0;
  let totalItems = 0;
  let totalShipping = 0;
  let DiscCoupon = 0;
  let finalTotal = 0;
  let totalMrp = 0;

  items.forEach((item) => {
    totalQuantity += item.quantity;
    totalAmount += item.totalPrice;
    totalShipping += item.quantity * item.shipping;
    totalMrp += item.quantity * item.mrp;

    // Apply free shipping if totalAmount > freeShipping
    totalShipping = totalAmount > freeShipping ? 0 : totalShipping;

    DiscCoupon = totalAmount > mindisCoupon ? maxCouponDisc : 0;
    finalTotal = totalAmount + totalShipping - DiscCoupon;
  });

  totalItems = items.length;
  return {
    totalQuantity,
    totalAmount,
    totalItems,
    totalShipping,
    totalMrp,
    DiscCoupon,
    finalTotal,
  };
};

const calculateSlabPrice = (price, quantity, quantitySlabs, discountSlabs) => {
  let finalPrice = price; // Initialize with original price

  // Loop through quantity slabs and check discount slabs
  for (let i = 0; i < quantitySlabs.length; i++) {
    // Apply discount if quantity is greater than or equal to the slab
    if (quantity >= quantitySlabs[i] && discountSlabs[i] !== 0) {
      finalPrice = price - (price * discountSlabs[i]) / 100; // Apply discount
    } else {
      break; // Stop applying discounts once quantity is smaller than the slab
    }
  }

  return finalPrice;
};

// Initial state
const initialState = {
  items: [],
  totalQuantity: 0,
  totalAmount: 0,
  totalItems: 0,
  totalShipping: 0,
  totalMrp: 0,
  totalDisCoupon: 0,
  totalFinalCart: 0,
  notification: null,
  status: 'idle',
  settings: {},
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    updateCartPrices: (state, action) => {
      state.items.forEach((item) => {
        const updatedData = action.payload[item.id]; // Get updated data for the item
        if (updatedData) {
          const { price, min, stock, mrp, bestRate } = updatedData;
          if (price !== undefined) item.price = price;
          if (min !== undefined) item.min = min;
          // Adjust quantity if it's less than the new min value
          if (item.quantity < min) {
            item.quantity = min;
          }
          if (stock !== undefined) item.stock = stock; 
          if (item.stock <= item.quantity){
            item.quantity = item.stock;
          }
          if (mrp !== undefined) item.mrp = mrp;
          if (bestRate !== undefined) item.bestRate = bestRate;
          const updatedPrice = calculateSlabPrice(
            item.price,
            item.quantity,
            item.quantitySlab,
            item.disPerc
          );
          item.totalPrice = updatedPrice * item.quantity;
        }
      });

      const {
        totalQuantity,
        totalAmount,
        totalItems,
        totalShipping,
        totalMrp,
        DiscCoupon,
        finalTotal,
      } = calculateTotals(state.items, state.settings.freeShipping);

      state.totalQuantity = totalQuantity;
      state.totalAmount = totalAmount;
      state.totalItems = totalItems;
      state.totalShipping = totalShipping;
      state.totalMrp = totalMrp;
      state.totalDisCoupon = DiscCoupon;
      state.totalFinalCart = finalTotal;
    },
    // Reducer for pushing new products into the cart
    // Reducer for pushing new products into the cart
    pushCart: (state, action) => {
      const newItems = action.payload;

      newItems.forEach(item => {
        const updatedPrice = calculateSlabPrice(
          item.price,
          item.quantity,
          item.quantitySlab,
          item.disPerc
        );

        state.items.push({
          ...item, // Spread the item to keep all its original properties
          totalPrice: item.quantity * updatedPrice, // Calculate total price using the updated price
        });
      });

        // Recalculate totals after adding the new product
      const {
        totalQuantity,
        totalAmount,
        totalItems,
        totalShipping,
        totalMrp,
        DiscCoupon,
        finalTotal,
      } = calculateTotals(state.items, state.settings.freeShipping);
    
      state.totalQuantity = totalQuantity;
      state.totalAmount = totalAmount;
      state.totalItems = totalItems;
      state.totalShipping = totalShipping;
      state.totalMrp = totalMrp;
      state.totalDisCoupon = DiscCoupon;
      state.totalFinalCart = finalTotal;
    },
    
    addItemToCart(state, action) {
      const newItem = action.payload;
      const existingItem = state.items.find((item) => item.id === newItem.id);
      const quantityToAdd = newItem.passedQuantity || (newItem.min > newItem.stock ? newItem.stock : newItem.min);

      if (!existingItem) {
        const updatedPrice = calculateSlabPrice(
          newItem.price,
          quantityToAdd,
          newItem.quantitySlab,
          newItem.disPerc
        );
        state.items.push({
          ...newItem,
          quantity: quantityToAdd,
          totalPrice: quantityToAdd * updatedPrice,
        });
      } else {
        if (existingItem.quantity + newItem.passedQuantity <= existingItem.stock) {
          existingItem.quantity += newItem.passedQuantity || existingItem.stock;
          const updatedPrice = calculateSlabPrice(
            existingItem.price,
            existingItem.quantity,
            existingItem.quantitySlab,
            existingItem.disPerc
          );
          existingItem.totalPrice = existingItem.quantity * updatedPrice;
        }
      }

      const {
        totalQuantity,
        totalAmount,
        totalItems,
        totalShipping,
        totalMrp,
        DiscCoupon,
        finalTotal,
      } = calculateTotals(state.items, state.settings.freeShipping);

      state.totalQuantity = totalQuantity;
      state.totalAmount = totalAmount;
      state.totalItems = totalItems;
      state.totalShipping = totalShipping;
      state.totalMrp = totalMrp;
      state.totalDisCoupon = DiscCoupon;
      state.totalFinalCart = finalTotal;
    },

    setNotification: (state, action) => {
      state.notification = action.payload;
    },

    clearNotification: (state) => {
      state.notification = null; // Clear notification
    },

    removeItemFromCart(state, action) {
      const id = action.payload;
      const existingItem = state.items.find((item) => item.id === id);

      if (existingItem) {
        state.items = state.items.filter((item) => item.id !== id);
      }

      const {
        totalQuantity,
        totalAmount,
        totalItems,
        totalShipping,
        totalMrp,
        DiscCoupon,
        finalTotal,
      } = calculateTotals(state.items, state.settings.freeShipping);

      state.totalQuantity = totalQuantity;
      state.totalAmount = totalAmount;
      state.totalItems = totalItems;
      state.totalShipping = totalShipping;
      state.totalMrp = totalMrp;
      state.totalDisCoupon = DiscCoupon;
      state.totalFinalCart = finalTotal;
    },

    updateItemQuantity(state, action) {
      const { id, quantity } = action.payload;
      const existingItem = state.items.find((item) => item.id === id);

      if (existingItem && quantity <= existingItem.stock) {
        existingItem.quantity = quantity;
        const updatedPrice = calculateSlabPrice(
          existingItem.price,
          existingItem.quantity,
          existingItem.quantitySlab,
          existingItem.disPerc
        );
        existingItem.totalPrice = existingItem.quantity * updatedPrice;
      }

      const {
        totalQuantity,
        totalAmount,
        totalItems,
        totalShipping,
        totalMrp,
        DiscCoupon,
        finalTotal,
      } = calculateTotals(state.items, state.settings.freeShipping);

      state.totalQuantity = totalQuantity;
      state.totalAmount = totalAmount;
      state.totalItems = totalItems;
      state.totalShipping = totalShipping;
      state.totalMrp = totalMrp;
      state.totalDisCoupon = DiscCoupon;
      state.totalFinalCart = Math.floor(finalTotal);
    },

    clearCart(state) {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      state.totalItems = 0;
      state.totalShipping = 0;
      state.totalMrp = 0;
      state.totalDisCoupon = 0;
      state.totalFinalCart = 0;
    },

    initializeCart(state) {
      const {
        totalQuantity,
        totalAmount,
        totalItems,
        totalShipping,
        totalMrp,
        DiscCoupon,
        finalTotal,
      } = calculateTotals(state.items, state.settings.freeShipping);

      state.totalQuantity = totalQuantity;
      state.totalAmount = totalAmount;
      state.totalItems = totalItems;
      state.totalShipping = totalShipping;
      state.totalMrp = totalMrp;
      state.totalDisCoupon = DiscCoupon;
      state.totalFinalCart = finalTotal;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchStoreSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStoreSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.settings = action.payload;
        state.freeShipping = action.payload.freeShipping; // Update freeShipping in state
      })
      .addCase(fetchStoreSettings.rejected, (state, action) => {
        state.status = 'failed';
        console.error("Failed to fetch settings:", action.error.message);
      });
  },
});

export const {
  addItemToCart,
  removeItemFromCart,
  updateItemQuantity,
  clearCart,
  updateCartPrices,
  setNotification,
  clearNotification,
  initializeCart,
  pushCart,
} = cartSlice.actions;

export default cartSlice.reducer;
export { CouponName };